import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import DeliverableGroup from './DeliverableGroup';
import { fetchProject } from '../api/projectApi';
import { useUploadContext } from '../context/UploadContext';
import { 
  getProjectStats, 
  getScoreTypes, 
  getDeliverablesByScoreType, 
  getScoreTypeExplanation 
} from '../data/mockProject';
import {
  getUploadLimitMessage,
  parsePreviewUploadLimit,
} from '../utils/uploadLimits';

const RecordingPortal = () => {
  const { slug, token } = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = token || null;
  const previewLimitReason = parsePreviewUploadLimit(
    searchParams.get('previewUploadLimit')
  );
  const cardPreviewLimitReason =
    previewLimitReason === 'file_too_large' || previewLimitReason === 'slot_locked'
      ? previewLimitReason
      : null;
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);
  const [activeTab, setActiveTab] = useState('introduction');
  
  // Data fetching state
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload context for duplicate detection
  const { initializeFromProject } = useUploadContext();

  // Load project data
  const loadProject = useCallback(async () => {
    try {
      const data = await fetchProject(slug, accessToken);
      if (!data) {
        setError('Project not found');
      } else {
        setProject(data);
        setError(null);
        // Initialize upload context with existing file hashes from server
        initializeFromProject(data.deliverables);
      }
    } catch (err) {
      console.error('Failed to load project:', err);
      setError('Failed to load project. Please try again.');
    }
  }, [slug, accessToken, initializeFromProject]);

  // Initial fetch
  useEffect(() => {
    async function initialLoad() {
      setLoading(true);
      await loadProject();
      setLoading(false);
    }
    initialLoad();
  }, [loadProject]);

  // Refresh project data after upload
  const handleUploadComplete = useCallback(() => {
    loadProject();
  }, [loadProject]);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Loading state
  if (loading) {
    return (
      <div className="portal-loading">
        <div className="loading-spinner" />
        <p>Loading project...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="portal-error">
        <h1>Oops!</h1>
        <p>{error}</p>
        <p className="error-slug">Looking for: <code>{slug}</code></p>
      </div>
    );
  }

  // No project
  if (!project) {
    return (
      <div className="portal-error">
        <h1>Project not found</h1>
        <p>The recording project you're looking for doesn't exist.</p>
      </div>
    );
  }

  // Compute derived data
  const stats = getProjectStats(project);
  const scoreTypes = getScoreTypes(project);

  // Live server flags win; preview query can still force a state for Step 1 demos
  const portalUploadBlockedReason =
    previewLimitReason === 'portal_full' || previewLimitReason === 'bucket_full'
      ? previewLimitReason
      : project.bucket_upload_blocked
        ? 'bucket_full'
        : project.portal_upload_blocked
          ? 'portal_full'
          : null;

  // Get data for active score type tab
  const activeScoreType = activeTab !== 'introduction' ? activeTab : null;
  const activeDeliverables = activeScoreType 
    ? getDeliverablesByScoreType(project, activeScoreType)
    : [];
  const activeExplanation = activeScoreType
    ? getScoreTypeExplanation(project, activeScoreType)
    : null;

  return (
    <div className={`recording-portal ${sidebarOpen ? 'sidebar-visible' : ''}`}>
      <Sidebar 
        project={project}
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        activeTab={activeTab}
        onTabClick={handleTabClick}
        scoreTypes={scoreTypes}
      />
      
      <div className="portal-content">
        {/* Mobile header */}
        <header className="mobile-header">
          <button className="menu-toggle" onClick={toggleSidebar} aria-label="Open the navigation menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="mobile-header-info">
            <span className="mobile-logo">AUDDICT</span>
            <span className="mobile-divider">·</span>
            <span className="mobile-project">{project.project_name}</span>
          </div>
          <div className="mobile-stats">
            <span className="mobile-stat-approved">{stats.approved}</span>
            <span className="mobile-stat-total"> of {stats.total}</span>
          </div>
        </header>

        <main className="portal-main">
          {previewLimitReason && (
            <div className="upload-limit-preview-banner" role="status">
              <strong>Upload limit preview</strong>
              <span>{getUploadLimitMessage(previewLimitReason)}</span>
              <span className="upload-limit-preview-hint">
                Open a score tab to see it on the upload box.
                Remove <code>?previewUploadLimit=…</code> when finished.
              </span>
            </div>
          )}

          {/* Introduction Tab */}
          {activeTab === 'introduction' && project.general_guidelines && (
            <section className="project-introduction">
              <h1 className="introduction-title">Recording Instructions</h1>
              <div className="introduction-content">
                {project.general_guidelines.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </section>
          )}

          {/* Score Type Group Tab */}
          {activeScoreType && (
            <div className="deliverables-container">
              <DeliverableGroup 
                scoreType={activeScoreType}
                deliverables={activeDeliverables}
                explanation={activeExplanation}
                project={project}
                slug={slug}
                accessToken={accessToken}
                onUploadComplete={handleUploadComplete}
                portalUploadBlockedReason={portalUploadBlockedReason}
                previewLimitReason={cardPreviewLimitReason}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default RecordingPortal;
